import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X, KeyRound, Search } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const ROLES = ["admin", "staff_vvip", "staff_camp", "camper"];
const ROLE_COLORS = { admin: "magenta", staff_vvip: "purple", staff_camp: "teal", camper: "yellow" };

const empty = {
  firstname: "", lastname: "", middlename: "", suffix: "",
  birthdate: "", gender: "", mobile_number: "", email: "", password: "",
  role: "camper", country: "Philippines", address: "",
  province: "", municipality: "", barangay: "",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // user object or "new"
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [resetTarget, setResetTarget] = useState(null); // user object being password-reset
  const [resetPw, setResetPw] = useState("");

  const load = () => api.get("/users").then(({ data }) => setUsers(data));

  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (u) => { setEditing(u.id); setForm({ ...empty, ...u, password: "" }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.firstname || !form.lastname || !form.email || !form.role) return toast.error("Name, email, role required");
    try {
      if (editing === "new") {
        if (!form.password) return toast.error("Password required for new users");
        await api.post("/users", form);
      } else {
        await api.put(`/users/${editing}`, form);
      }
      toast.success("Saved");
      cancel();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const submitReset = async () => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      toast.success(`New password sent to ${resetTarget.email}`);
      setResetTarget(null);
      setResetPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = [u.name, u.email, u.firstname, u.lastname, u.mobile_number].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker="System Users" title="User Management" />
        {!editing && <BrutalButton variant="primary" onClick={startNew} data-testid="user-add"><Plus className="h-4 w-4" /> Add User</BrutalButton>}
      </div>

      <BrutalCard className="p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B4E9E]" />
          <BrutalInput className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, mobile..." data-testid="users-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {["all", ...ROLES].map((r) => (
            <button key={r} onClick={() => setRoleFilter(r)} className={`px-3 py-2 border-2 border-[#3D1E6D] text-[10px] font-bold uppercase tracking-widest ${
              roleFilter === r ? "bg-[#E5247C] text-white shadow-[3px_3px_0_#3D1E6D]" : "bg-white"
            }`} data-testid={`user-filter-${r}`}>{r}</button>
          ))}
        </div>
      </BrutalCard>

      {editing && (
        <BrutalCard className="p-5" data-testid="user-form">
          <h3 className="display text-2xl mb-3">{editing === "new" ? "Add User" : "Edit User"}</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="First Name *"><BrutalInput value={form.firstname} onChange={(e) => setForm({...form, firstname: e.target.value})} data-testid="user-firstname" /></Field>
            <Field label="Middle Name"><BrutalInput value={form.middlename} onChange={(e) => setForm({...form, middlename: e.target.value})} /></Field>
            <Field label="Last Name *"><BrutalInput value={form.lastname} onChange={(e) => setForm({...form, lastname: e.target.value})} data-testid="user-lastname" /></Field>
            <Field label="Suffix"><BrutalInput value={form.suffix} onChange={(e) => setForm({...form, suffix: e.target.value})} /></Field>
            <Field label="Birthdate"><BrutalInput type="date" value={form.birthdate} onChange={(e) => setForm({...form, birthdate: e.target.value})} /></Field>
            <Field label="Gender">
              <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.gender} onChange={(e) => setForm({...form, gender: e.target.value})}>
                <option value="">—</option>
                {["Male","Female","Non-binary","Prefer not to say"].map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Email *"><BrutalInput type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email" /></Field>
            <Field label="Mobile Number"><BrutalInput value={form.mobile_number} onChange={(e) => setForm({...form, mobile_number: e.target.value})} data-testid="user-mobile" /></Field>
            <Field label="Role *">
              <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="user-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            {editing === "new" && (
              <Field label="Password *"><BrutalInput type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="user-password" /></Field>
            )}
            <Field label="Country"><BrutalInput value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} /></Field>
            <Field label="Address"><BrutalInput value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></Field>
          </div>
          <div className="mt-4 flex gap-2">
            <BrutalButton variant="primary" onClick={save} data-testid="user-save"><Save className="h-4 w-4" /> Save</BrutalButton>
            <BrutalButton variant="outline" onClick={cancel}><X className="h-4 w-4" /> Cancel</BrutalButton>
          </div>
        </BrutalCard>
      )}

      <BrutalCard className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#FFB400]">
            <tr>
              {["Name","Email","Mobile","Role","Verified","Created","Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u, i) => (
              <tr key={u.id} className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"} data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 font-bold">{u.name || `${u.firstname} ${u.lastname}`}</td>
                <td className="px-3 py-2 text-[#6B4E9E]">{u.email}</td>
                <td className="px-3 py-2 text-[#6B4E9E]">{u.mobile_number}</td>
                <td className="px-3 py-2"><Badge color={ROLE_COLORS[u.role]}>{u.role}</Badge></td>
                <td className="px-3 py-2">{u.email_verified ? <Badge color="green">YES</Badge> : <Badge color="red">NO</Badge>}</td>
                <td className="px-3 py-2 text-xs">{u.created_at && new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(u)} className="kf-border bg-white p-1.5" data-testid={`user-edit-${u.id}`} title="Edit"><Pencil className="h-3 w-3" /></button>
                    <button onClick={() => { setResetTarget(u); setResetPw(""); }} className="kf-border bg-[#FFB400] p-1.5" data-testid={`user-reset-${u.id}`} title="Reset password"><KeyRound className="h-3 w-3" /></button>
                    <button onClick={() => remove(u.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`user-delete-${u.id}`} title="Delete"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#6B4E9E]">No users match.</td></tr>}
          </tbody>
        </table>
      </BrutalCard>

      {resetTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setResetTarget(null)}
          data-testid="reset-pw-modal"
        >
          <BrutalCard className="w-full max-w-md p-5 bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="display text-xl text-[#3D1E6D]">Reset Password</div>
              <button onClick={() => setResetTarget(null)} className="kf-border bg-white p-1.5 hover:bg-[#FFB400]" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-[#6B4E9E] mb-3">
              Setting a new password for <b>{resetTarget.email}</b>. A mock email will deliver the new password to the user.
            </p>
            <BrutalLabel>New Password (min 6 chars)</BrutalLabel>
            <BrutalInput
              type="text"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              placeholder="Enter new password"
              data-testid="reset-pw-input"
              autoFocus
            />
            <div className="mt-4 flex gap-2">
              <BrutalButton variant="primary" onClick={submitReset} data-testid="reset-pw-submit">
                Confirm & Send Email
              </BrutalButton>
              <BrutalButton variant="outline" onClick={() => setResetTarget(null)}>Cancel</BrutalButton>
            </div>
          </BrutalCard>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);
